import React, { useState, useEffect } from 'react';

interface Horse {
  id: string;
  name: string;
  designation: string;
  status: string;
}

interface Family {
  id: string;
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
}

interface AddRanchGirlFormProps {
  onClose: () => void;
  onSuccess: () => void;
  preselectedFamilyId?: string;
}

interface RanchGirlFormData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  familyId: string;
  skillLevel: string;
  assignedHorseId: string;
  boardPosition: string;
  graduationYear: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  allergies: string;
  medicalNotes: string;
  parentalConsent: boolean;
  liabilityWaiverSigned: boolean;
}

export default function AddRanchGirlForm({ onClose, onSuccess, preselectedFamilyId }: AddRanchGirlFormProps) {
  const [families, setFamilies] = useState<Family[]>([]);
  const [horses, setHorses] = useState<Horse[]>([]);
  const [formData, setFormData] = useState<RanchGirlFormData>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    familyId: preselectedFamilyId || '',
    skillLevel: 'Non Basic',
    assignedHorseId: '',
    boardPosition: '',
    graduationYear: new Date().getFullYear() + 4 + '', // Default to 4 years from now
    emergencyContactName: '',
    emergencyContactPhone: '',
    allergies: '',
    medicalNotes: '',
    parentalConsent: false,
    liabilityWaiverSigned: false
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    fetchFamilies();
    fetchHorses();
  }, []);

  const fetchFamilies = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setFamilies(data.data);
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    }
  };

  const fetchHorses = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/horses', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setHorses(data.data);
      }
    } catch (error) {
      console.error('Error fetching horses:', error);
    }
  };

  const handleInputChange = (field: keyof RanchGirlFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/ranchgirls', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        onSuccess();
      } else {
        setErrors(data.errors?.map((err: any) => err.msg) || [data.message]);
      }
    } catch (error) {
      console.error('Error creating ranch girl:', error);
      setErrors(['Failed to create ranch girl. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
      <div className="bg-white rounded-t-xl sm:rounded-xl max-w-4xl w-full max-h-[92vh] sm:max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Add New Ranch Girl</h3>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="text-red-800 font-medium mb-2">Please fix the following errors:</h4>
                <ul className="list-disc list-inside text-red-700 text-sm">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Personal Information */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.dateOfBirth}
                      onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Family <span className="text-red-500">*</span>
                    </label>
                    <select
                      required
                      value={formData.familyId}
                      onChange={(e) => handleInputChange('familyId', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      disabled={!!preselectedFamilyId}
                    >
                      <option value="">Select a family</option>
                      {families.map((family) => (
                        <option key={family.id} value={family.id}>
                          {family.familyName} ({family.primaryContactFirstName} {family.primaryContactLastName})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Skill Level
                      </label>
                      <select
                        value={formData.skillLevel}
                        onChange={(e) => handleInputChange('skillLevel', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      >
                                        <option value="Non Basic">Non Basic</option>
                <option value="Basic">Basic</option>
                <option value="Third Class">Third Class</option>
                <option value="Second Class">Second Class</option>
                <option value="First Class">First Class</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Graduation Year
                      </label>
                      <input
                        type="number"
                        min={new Date().getFullYear()}
                        max={new Date().getFullYear() + 10}
                        value={formData.graduationYear}
                        onChange={(e) => handleInputChange('graduationYear', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned Horse (Optional)
                    </label>
                    <select
                      value={formData.assignedHorseId}
                      onChange={(e) => handleInputChange('assignedHorseId', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    >
                      <option value="">No horse assigned</option>
                      {horses.map(horse => (
                        <option key={horse.id} value={horse.id}>
                          {horse.name} - {horse.designation} ({horse.status})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Board Position (Optional)
                    </label>
                    <select
                      value={formData.boardPosition}
                      onChange={(e) => handleInputChange('boardPosition', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                    >
                      <option value="">None</option>
                      <option value="President">President</option>
                      <option value="Vice President">Vice President</option>
                      <option value="Secretary">Secretary</option>
                      <option value="Treasurer">Treasurer</option>
                      <option value="Horse Chair">Horse Chair</option>
                      <option value="Historian">Historian</option>
                      <option value="Drill Master">Drill Master</option>
                      <option value="Sgt. at Arms">Sgt. at Arms</option>
                      <option value="Co-Historian">Co-Historian</option>
                      <option value="Activities Chair">Activities Chair</option>
                      <option value="Fundraising Chair">Fundraising Chair</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Emergency Contact & Medical */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact & Medical</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Contact Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.emergencyContactName}
                      onChange={(e) => handleInputChange('emergencyContactName', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="Full name of emergency contact"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Contact Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.emergencyContactPhone}
                      onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Allergies
                    </label>
                    <textarea
                      value={formData.allergies}
                      onChange={(e) => handleInputChange('allergies', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      rows={3}
                      placeholder="List any known allergies (food, environmental, etc.)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Medical Notes
                    </label>
                    <textarea
                      value={formData.medicalNotes}
                      onChange={(e) => handleInputChange('medicalNotes', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                      rows={3}
                      placeholder="Any medical conditions, medications, or special considerations"
                    />
                  </div>

                  {/* Legal Checkboxes */}
                  <div className="space-y-3 pt-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="parentalConsent"
                        checked={formData.parentalConsent}
                        onChange={(e) => handleInputChange('parentalConsent', e.target.checked)}
                        className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                      />
                      <label htmlFor="parentalConsent" className="ml-2 block text-sm text-gray-900">
                        Parental Consent Obtained <span className="text-red-500">*</span>
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="liabilityWaiver"
                        checked={formData.liabilityWaiverSigned}
                        onChange={(e) => handleInputChange('liabilityWaiverSigned', e.target.checked)}
                        className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                      />
                      <label htmlFor="liabilityWaiver" className="ml-2 block text-sm text-gray-900">
                        Liability Waiver Signed <span className="text-red-500">*</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.parentalConsent || !formData.liabilityWaiverSigned}
                className="px-6 py-2 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Add Ranch Girl'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
} 